import { useId } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AffiliatePerformance } from '@/api/generated';
import { MoneyCell } from '@/components/cells';

export function AffiliatePerformancePanel({
  performance,
}: {
  performance: AffiliatePerformance[];
}) {
  const { t } = useTranslation();
  const headingId = useId();
  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Typography variant="h3" component="h3" id={headingId}>
        {t('affiliates.performance.title')}
      </Typography>
      {performance.length ? (
        <TableContainer>
          {/* Named by the heading; the caption says how the figures are counted. */}
          <Table size="small" aria-labelledby={headingId}>
            <caption>{t('affiliates.performance.basis')}</caption>
            <TableHead>
              <TableRow>
                <TableCell>{t('affiliates.performance.currency')}</TableCell>
                {['revenue30d', 'revenueToDate', 'commission30d', 'commissionToDate'].map((key) => (
                  <TableCell key={key} align="right">
                    {t(`affiliates.performance.${key}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {performance.map((row) => (
                <TableRow key={row.currency}>
                  <TableCell component="th" scope="row">
                    {row.currency}
                  </TableCell>
                  <MoneyCell minor={row.referredRevenue30dMinor} currency={row.currency} />
                  <MoneyCell minor={row.referredRevenueToDateMinor} currency={row.currency} />
                  <MoneyCell minor={row.commission30dMinor} currency={row.currency} />
                  <MoneyCell minor={row.commissionToDateMinor} currency={row.currency} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <>
          <Typography sx={{ mt: 1 }}>{t('affiliates.performance.empty')}</Typography>
          <Typography variant="caption">{t('affiliates.performance.basis')}</Typography>
        </>
      )}
    </Paper>
  );
}
